import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  SelectChangeEvent,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useGetCommandCatalogQuery, useQueueDeviceCommandMutation } from '@store/api/commandApi';
import { useGetHealthConfigQuery } from '@store/api/systemApi';
import { fontFamilyMono } from '../../theme/theme';

interface SendCommandDialogProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  deviceName?: string;
  sshTunnelPort?: number;
}

interface CommandFormData {
  command_type: string;
  parameters: {
    duration?: number;
    extension?: number;
    script_content?: string;
    force_new?: boolean;
    ssh_tunnel_tls?: boolean;
    regenerate?: boolean;
    /** Required parameters of command types without a bespoke field below. */
    [param: string]: string | number | boolean | undefined;
  };
  priority: number;
  timeout: number;
}

/** Words the title-caser must not sentence-case. */
const ACRONYMS = new Set(['ssh', 'dps', 'tls']);

/**
 * Turn a command type into a menu label: `setup_ssh_tunnel` -> "Setup SSH Tunnel".
 * Purely cosmetic - which command types exist comes from the Command Catalog, so
 * a newly catalogued type shows up here without anyone editing this file.
 */
const formatCommandLabel = (commandType: string): string =>
  commandType
    .split('_')
    .map((word) => (ACRONYMS.has(word) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');

const SendCommandDialog: React.FC<SendCommandDialogProps> = ({
  open,
  onClose,
  deviceId,
  deviceName,
  sshTunnelPort,
}) => {
  const [formData, setFormData] = useState<CommandFormData>({
    command_type: 'setup_ssh_tunnel',
    parameters: {
      duration: 3600,
    },
    priority: 5,
    timeout: 300,
  });
  const [error, setError] = useState<string | null>(null);

  const [queueCommand, { isLoading }] = useQueueDeviceCommandMutation();

  // The Command Catalog decides which command types exist and which of them an
  // operator may send (only while the dialog is open).
  const { data: catalog } = useGetCommandCatalogQuery(undefined, { skip: !open });
  const operatorCommands = Object.entries(catalog ?? {}).filter(
    ([, entry]) => entry.operator_issuable,
  );
  const selectedEntry = catalog?.[formData.command_type];

  // Fetch SSH tunnel configuration from backend (only while the dialog is open)
  const { data: healthConfig, isError: healthConfigError } = useGetHealthConfigQuery(undefined, {
    skip: !open,
  });
  const sshConfig = healthConfig
    ? { host: healthConfig.ssh_tunnel_host, port: healthConfig.ssh_tunnel_port }
    : healthConfigError
      ? {
          // Fallback to defaults
          host: window.location.hostname === 'localhost' ? 'ssh-tunnel-server' : window.location.hostname,
          port: 2222,
        }
      : null;

  const handleCommandTypeChange = (event: SelectChangeEvent) => {
    const newType = event.target.value;
    setFormData((prev) => ({
      ...prev,
      command_type: newType,
      parameters: getDefaultParameters(newType),
    }));
    setError(null);
  };

  const getDefaultParameters = (commandType: string) => {
    switch (commandType) {
      case 'setup_ssh_tunnel':
        return { duration: 3600 };
      case 'extend_ssh_tunnel':
        return { extension: 30 };
      case 'execute_script':
        return { script_content: '' };
      case 'get_ssh_key':
        return { regenerate: false };
      default:
        return {};
    }
  };

  const handleParameterChange = (param: string, value: string | number | boolean) => {
    setFormData((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [param]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    try {
      setError(null);

      // Validate execute_script has non-empty script_content
      if (formData.command_type === 'execute_script') {
        const scriptContent = formData.parameters.script_content?.trim();
        if (!scriptContent) {
          setError('Script content cannot be empty');
          return;
        }
      }

      // The Catalog knows what each command type needs. setup_ssh_tunnel and
      // extend_ssh_tunnel fill theirs in below, so they are exempt.
      const filledInOnSubmit = ['setup_ssh_tunnel', 'extend_ssh_tunnel'];
      if (selectedEntry && !filledInOnSubmit.includes(formData.command_type)) {
        const missing = selectedEntry.required_params.filter((param) => {
          const value = formData.parameters[param];
          return value === undefined || value === '';
        });
        if (missing.length > 0) {
          setError(`Missing required parameter${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`);
          return;
        }
      }

      // Clean up parameters - remove empty values
      let parameters = Object.entries(formData.parameters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string | number | boolean>);

      // Add SSH server details for setup_ssh_tunnel commands
      if (formData.command_type === 'setup_ssh_tunnel') {
        // Use ssh-tunnel-server for localhost (local dev), otherwise use current hostname
        // Use SSH config from backend
        const sshHost = sshConfig?.host || (
          window.location.hostname === 'localhost'
            ? 'ssh-tunnel-server'
            : window.location.hostname
        );
        const sshPort = sshConfig?.port || 2222;

        parameters = {
          ...parameters,
          local_port: 22, // Always forward SSH port 22
          ssh_host: sshHost,
          ssh_port: sshPort,
          ssh_user: `iot-${deviceId.substring(0, 8)}`,
        };
      }

      // Add remote_port for extend_ssh_tunnel commands
      if (formData.command_type === 'extend_ssh_tunnel') {
        if (!sshTunnelPort) {
          setError('Cannot extend SSH tunnel: No active tunnel port found for this device');
          return;
        }
        parameters = {
          ...parameters,
          remote_port: sshTunnelPort,
        };
      }

      await queueCommand({
        deviceId,
        command: {
          command_type: formData.command_type,
          parameters,
          priority: formData.priority,
          timeout: formData.timeout,
        },
      }).unwrap();

      onClose();
    } catch (err) {
      const error = err as { data?: { detail?: string } };
      setError(error?.data?.detail || 'Failed to send command');
    }
  };

  const renderParameterFields = () => {
    switch (formData.command_type) {
      case 'setup_ssh_tunnel':
        return (
          <>
            <TextField
              fullWidth
              label="Duration (hours)"
              type="number"
              value={(formData.parameters.duration || 3600) / 3600}
              onChange={(e) => handleParameterChange('duration', parseInt(e.target.value, 10) * 3600)}
              helperText="How long to keep the tunnel open (default: 1 hour)"
              margin="normal"
              slotProps={{
                input: { inputProps: { min: 1, max: 24 } }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.parameters.force_new || false}
                  onChange={(e) => handleParameterChange('force_new', e.target.checked)}
                />
              }
              label="Force new tunnel (kills existing tunnels - use after network changes)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.parameters.ssh_tunnel_tls || false}
                  onChange={(e) => handleParameterChange('ssh_tunnel_tls', e.target.checked)}
                />
              }
              label="Tunnel over TLS (for customer sites that block/inspect SSH on 443)"
            />
            {formData.parameters.ssh_tunnel_tls && (
              <Alert severity="info" sx={{ mt: 1 }}>
                The SSH tunnel will be wrapped in TLS so it looks like HTTPS to firewalls. Enable this
                only for devices whose site blocks the default SSH tunnel. The TLS SNI is set by the
                server/device configuration.
              </Alert>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              {sshTunnelPort ? (
                <>
                  This device will use port <strong>{sshTunnelPort}</strong> on the SSH tunnel server.
                  <br />
                  Once established, connect using: <code>ssh -p {sshTunnelPort} cnservice@tunnel-server</code>
                </>
              ) : (
                'A port will be automatically allocated for this device on the SSH tunnel server.'
              )}
            </Alert>
            {formData.parameters.force_new && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Force mode will kill any existing autossh/ssh processes for this tunnel before establishing a new connection.
              </Alert>
            )}
          </>
        );
      case 'extend_ssh_tunnel':
        return (
          <>
            <TextField
              fullWidth
              label="Extension (minutes)"
              type="number"
              value={formData.parameters.extension || 30}
              onChange={(e) => handleParameterChange('extension', parseInt(e.target.value, 10))}
              helperText="How many minutes to extend the tunnel (default: 30)"
              margin="normal"
              slotProps={{
                input: { inputProps: { min: 15, max: 240 } }
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              {sshTunnelPort ? (
                <>
                  This will extend the existing SSH tunnel on port <strong>{sshTunnelPort}</strong> by <strong>{formData.parameters.extension || 30}</strong> minutes.
                </>
              ) : (
                <>
                  This will extend the existing SSH tunnel by <strong>{formData.parameters.extension || 30}</strong> minutes.
                  <br />
                  Make sure there is an active tunnel before extending.
                </>
              )}
            </Alert>
          </>
        );
      case 'execute_script':
        return (
          <TextField
            fullWidth
            label="Script"
            multiline
            rows={6}
            value={formData.parameters.script_content || ''}
            onChange={(e) => handleParameterChange('script_content', e.target.value)}
            helperText="Bash script to execute on the device"
            margin="normal"
            // On the textarea, not the TextField root: MUI sets an explicit
            // fontFamily on InputBase, so an `sx` here would never reach it.
            slotProps={{ htmlInput: { style: { fontFamily: fontFamilyMono } } }}
          />
        );
      case 'reboot':
        return (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will restart the device. The device will be offline temporarily.
          </Alert>
        );
      case 'get_ssh_key':
        return (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.parameters.regenerate || false}
                  onChange={(e) => handleParameterChange('regenerate', e.target.checked)}
                />
              }
              label="Regenerate SSH key (create new key instead of fetching existing)"
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              This command will fetch the device's SSH public key and update it on the server.
              {formData.parameters.regenerate && ' A new SSH key pair will be generated on the device.'}
            </Alert>
          </>
        );
      default:
        // No bespoke fields for this command type, so ask for whatever the
        // Catalog says it requires. A newly catalogued command is usable here
        // the moment the server serves it.
        return (
          <>
            {selectedEntry?.required_params.map((param) => (
              <TextField
                key={param}
                fullWidth
                required
                label={param}
                value={formData.parameters[param] ?? ''}
                onChange={(e) => handleParameterChange(param, e.target.value)}
                margin="normal"
              />
            ))}
            {selectedEntry && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {selectedEntry.description}
              </Alert>
            )}
          </>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Send Command to {deviceName || `Device ${deviceId}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel>Command Type</InputLabel>
            <Select
              value={formData.command_type}
              onChange={handleCommandTypeChange}
              label="Command Type"
            >
              {operatorCommands.map(([commandType]) => (
                <MenuItem key={commandType} value={commandType}>
                  {formatCommandLabel(commandType)}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {selectedEntry?.description}
            </FormHelperText>
          </FormControl>

          {renderParameterFields()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Command'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendCommandDialog;
